import { useRef, useState, useCallback } from 'react';
import { HOUR_PX } from './timeGridMetrics';

/**
 * useCalendarCreateDnD (개선안 #19) — 시간격자(주/일 뷰) 빈 슬롯 드래그로 새 일정 생성.
 *
 * useCalendarDnD(기존 이동 드래그, T-cal21)와 독립된 별도 훅 — 상태를 공유하지 않으므로
 * 서로 간섭하지 않는다. 호출부(TimeGridView)가 이벤트 블록이 아닌 빈 배경(시간눈금 칸)에만
 * 이 훅의 핸들러를 건다 — 이벤트 블록은 별도 DOM 형제 요소라 pointerdown 이 여기로 버블되지
 * 않는다(이동 드래그와 자연히 충돌 없음).
 *
 * ⚠ 예전엔 pointerdown 즉시 setPointerCapture 를 걸었는데, 이러면 사용자가 빈 배경(그리드
 * 대부분)에서 좌우로 스와이프를 시작하는 순간 이 훅이 포인터를 선점해버려서 좌우 기간전환
 * 제스처가 꼬였다(당시엔 useSwipeNav 캐러셀과 충돌 — b87e1c8b). 그래서 축 판정을 늦춘다 —
 * AXIS_THRESHOLD_PX 를 넘기 전엔 아무것도 안 하고, 넘는 순간 가로가 더 크면(좌우 이동으로
 * 판단) 이 훅은 관여를 접고 완전히 손을 뗀다(캡처도 preventDefault 도 안 걸었으므로 뗄 것도
 * 없음 — 이벤트가 그대로 버블돼 브라우저 네이티브 처리로 넘어간다). 세로가 더 크면(기존처럼
 * 슬롯 드래그) 그때 캡처를 걸고 기존 로직을 이어간다.
 *
 * 2026-08-21 — 주/일 뷰가 좌우 드래그 캐러셀(useSwipeNav)에서 가로 연속 스크롤(네이티브
 * overflow-x, TimeGridView.tsx 의 .calendar-timegrid)로 바뀌면서 useSwipeNav 자체는
 * 삭제됐다. 이 훅의 판정 로직은 무수정 — 가로로 판정될 때 그냥 손을 떼는 동작이 원래도
 * "옆에 누가 처리하든 상관없이 네이티브에 맡긴다"였으므로, 그 "누가"가 별도 훅(캡처+애니메이션)
 * 에서 브라우저의 진짜 네이티브 스크롤로 바뀌었을 뿐 이 파일이 협조해야 할 계약은 그대로다.
 */
const SNAP_MIN = 5; // 제안서 요구 — 5분 단위 스냅(기존 이동 드래그의 15분과는 별개).
const AXIS_THRESHOLD_PX = 8; // 주/일 뷰의 useContinuousDays 가로 스크롤과 같은 제스처를 같은 시점에 갈라야 한다.

/** day column 상단 기준 clientY → 5분 스냅된 "day-start 로부터의 분". pure — 테스트 가능. */
export function pxToSnappedDayMinutes(clientY: number, colTopPx: number, hourPx = HOUR_PX, snapMin = SNAP_MIN): number {
  const rawMin = ((clientY - colTopPx) / hourPx) * 60;
  return Math.round(rawMin / snapMin) * snapMin;
}

export interface CreateDragState {
  dayIndex: number;
  startMin: number; // day-start(00:00) 기준 분, 스냅됨. 항상 endMin 보다 작다.
  endMin: number;
}

interface UseCalendarCreateDnDArgs {
  // 드래그 종료 시 호출 — 부모가 생성 모달을 연다. dayIndex + day-start 기준 분(시작/끝).
  onCreate: (dayIndex: number, startMin: number, endMin: number) => void;
}

export function useCalendarCreateDnD({ onCreate }: UseCalendarCreateDnDArgs) {
  const [drag, setDrag] = useState<CreateDragState | null>(null);
  const colTopRef = useRef(0);
  const anchorMinRef = useRef(0);
  const dayIndexRef = useRef(-1);
  const movedRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  // pending = pointerdown 은 됐지만 아직 가로/세로 축을 못 가른 상태(캡처 전).
  // engaged = 세로로 확정돼 실제로 슬롯 드래그를 진행 중인 상태(캡처함).
  const pendingRef = useRef(false);
  const engagedRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent, dayIndex: number, colTopPx: number) => {
    if (e.button !== 0) return; // 좌클릭/터치만.
    colTopRef.current = colTopPx;
    dayIndexRef.current = dayIndex;
    movedRef.current = false;
    pendingRef.current = true;
    engagedRef.current = false;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    anchorMinRef.current = pxToSnappedDayMinutes(e.clientY, colTopPx);
    // 캡처는 축이 세로로 확정된 뒤(onPointerMove)로 미룬다 — 좌우 스와이프와의 충돌 수정,
    // 파일 상단 주석 참조.
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (pendingRef.current) {
      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;
      if (Math.abs(dx) < AXIS_THRESHOLD_PX && Math.abs(dy) < AXIS_THRESHOLD_PX) return; // 아직 축 미확정.
      pendingRef.current = false;
      if (Math.abs(dx) > Math.abs(dy)) {
        // 가로가 더 큼 — 좌우 스와이프(기간 전환)로 판단, 이 훅은 관여하지 않는다.
        dayIndexRef.current = -1;
        return;
      }
      engagedRef.current = true;
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* */ }
    }
    if (!engagedRef.current) return;
    const curMin = pxToSnappedDayMinutes(e.clientY, colTopRef.current);
    if (Math.abs(curMin - anchorMinRef.current) >= SNAP_MIN) movedRef.current = true;
    const lo = Math.min(anchorMinRef.current, curMin);
    const hi = Math.max(anchorMinRef.current, curMin);
    setDrag({ dayIndex: dayIndexRef.current, startMin: lo, endMin: Math.max(hi, lo + SNAP_MIN) });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const wasEngaged = engagedRef.current;
    if (!pendingRef.current && !wasEngaged) return; // 이 훅과 무관한(가로로 넘어간) 제스처.
    if (wasEngaged) { try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* */ } }
    const d = drag;
    const moved = movedRef.current;
    dayIndexRef.current = -1;
    movedRef.current = false;
    pendingRef.current = false;
    engagedRef.current = false;
    setDrag(null);
    // 실제로 끌었을 때만(단순 클릭은 생성 안 함 — 빈 배경 클릭은 아무 동작 없음이 기존 동작).
    if (wasEngaged && d && moved) onCreate(d.dayIndex, d.startMin, d.endMin);
  }, [drag, onCreate]);

  return {
    drag,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
  };
}
